import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiCalendar, FiPlus, FiVideo, FiPlay, FiSquare, FiClock, FiUsers } from 'react-icons/fi';
import { communicationsAPI, subjectsAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useAppConfig } from '../../contexts/AppConfigContext';
import './Meetings.css';

const Meetings = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { appName } = useAppConfig();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [meetings, setMeetings] = useState([]);
  const [filter, setFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createSaving, setCreateSaving] = useState(false);
  const [inviteesList, setInviteesList] = useState([]);
  const [inviteesLoading, setInviteesLoading] = useState(false);
  const [inviteesError, setInviteesError] = useState('');
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [form, setForm] = useState({
    type: 'parent_teacher',
    title: '',
    subject: '',
    description: '',
    scheduledAt: '',
    durationMinutes: 60,
    invitees: [],
    allowAnyStudent: false
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const resp = await communicationsAPI.getMeetings();
      const data = resp?.data?.data ?? resp?.data ?? {};
      const raw = data?.meetings ?? data;
      const list = Array.isArray(raw) ? raw : [];
      setMeetings(list);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) setError('Please sign in to view meetings.');
      else if (status === 403) setError('Your account is not authorized to view meetings.');
      else setError(e?.response?.data?.message || 'Failed to load meetings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    const fetchSubjects = async () => {
      try {
        const res = await subjectsAPI.getSubjectNames();
        const names = res?.data?.data?.names ?? res?.data?.names ?? [];
        if (!cancelled && Array.isArray(names)) setSubjectOptions(names);
      } catch {
        if (!cancelled) setSubjectOptions([]);
      }
    };
    fetchSubjects();
    return () => { cancelled = true; };
  }, []);

  const canCreateMeeting = user?.role === 'teacher' || user?.role === 'admin';
  const canCreateStaff = user?.role === 'admin';
  const canEndMeeting = user?.role === 'teacher' || user?.role === 'admin';

  const filteredMeetings = Array.isArray(meetings)
    ? meetings.filter((m) => (filter === 'all' ? true : (m.status || '').toLowerCase() === filter.toLowerCase()))
    : [];

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getStatusBadge = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'scheduled') return 'bg-blue-100 text-blue-800';
    if (s === 'live') return 'bg-emerald-100 text-emerald-800';
    if (s === 'completed') return 'bg-slate-100 text-slate-700';
    if (s === 'cancelled') return 'bg-red-100 text-red-800';
    return 'bg-slate-100 text-slate-700';
  };

  const getStatusText = (status) => {
    const map = { scheduled: 'Scheduled', live: 'Live', completed: 'Completed', cancelled: 'Cancelled' };
    return map[(status || '').toLowerCase()] || status || '—';
  };

  const openCreateModal = useCallback(() => {
    setShowCreateModal(true);
    setInviteesError('');
    setForm({
      type: 'parent_teacher',
      title: '',
      subject: '',
      description: '',
      scheduledAt: '',
      durationMinutes: 60,
      invitees: [],
      allowAnyStudent: false
    });
  }, []);

  const fetchInviteesForType = useCallback(async (type) => {
    setInviteesLoading(true);
    setInviteesError('');
    try {
      const res = await communicationsAPI.getPossibleInvitees(type);
      const raw = res?.data;
      const list = Array.isArray(raw?.data) ? raw.data : (Array.isArray(raw) ? raw : []);
      setInviteesList(list);
    } catch (err) {
      setInviteesList([]);
      setInviteesError(err.response?.data?.message || 'Failed to load invitees');
      showError(err.response?.data?.message || 'Failed to load invitees');
    } finally {
      setInviteesLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    if (showCreateModal && form.type) fetchInviteesForType(form.type);
  }, [showCreateModal, form.type, fetchInviteesForType]);

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { showError('Title is required'); return; }
    if (!form.scheduledAt) { showError('Date and time is required'); return; }
    setCreateSaving(true);
    try {
      await communicationsAPI.createMeeting({
        type: form.type,
        title: form.title.trim(),
        subject: (form.subject || '').trim(),
        description: (form.description || '').trim(),
        scheduledAt: new Date(form.scheduledAt).toISOString(),
        durationMinutes: form.durationMinutes || 60,
        invitees: form.invitees,
        allowAnyStudent: !!form.allowAnyStudent
      });
      showSuccess('Meeting created');
      setShowCreateModal(false);
      load();
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to create meeting');
    } finally {
      setCreateSaving(false);
    }
  };

  const toggleInvitee = (id) => {
    setForm((prev) => ({
      ...prev,
      invitees: prev.invitees.includes(id) ? prev.invitees.filter((x) => x !== id) : [...prev.invitees, id]
    }));
  };

  const handleEndMeeting = async (e, meeting) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canEndMeeting || !window.confirm('End this meeting? It will be marked as completed.')) return;
    try {
      await communicationsAPI.updateMeetingStatus(meeting.id, 'completed');
      showSuccess('Meeting ended');
      load();
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to end meeting');
    }
  };

  const getActionLabel = (m) => (m.status === 'scheduled' ? 'Start' : 'Join');

  if (loading) {
    return (
      <div className="ts-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="ts-page">
      <header className="ts-header">
        <div className="ts-header-row">
          <div>
            <h1 className="ts-title">{appName ? `${appName} Meetings` : 'Meetings'}</h1>
            <p className="ts-subtitle">Video meetings and parent–teacher or staff calls</p>
          </div>
          {canCreateMeeting && (
            <div className="ts-actions">
              <button type="button" onClick={openCreateModal} className="ts-btn-primary">
                <FiPlus size={16} /> Create meeting
              </button>
            </div>
          )}
        </div>
      </header>

      {error && <div className="ts-error">{error}</div>}

      <div className="ts-filters">
        {['all', 'scheduled', 'live', 'completed', 'cancelled'].map((f) => (
          <button key={f} type="button" onClick={() => setFilter(f)} className={`ts-filter-btn ${filter === f ? 'active' : ''}`}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {filteredMeetings.length === 0 ? (
        <div className="ts-empty">
          <FiCalendar size={40} className="ts-empty-icon" style={{ margin: '0 auto' }} />
          <p>{canCreateMeeting ? 'Create a meeting or change the filter.' : 'No meetings match the selected filter.'}</p>
          {canCreateMeeting && (
            <button type="button" onClick={openCreateModal} className="ts-btn-primary" style={{ margin: '0 auto' }}>
              <FiPlus size={16} /> Create meeting
            </button>
          )}
        </div>
      ) : (
        <div className="ts-grid">
          {filteredMeetings.map((m) => {
            const inviteeCount = Array.isArray(m.invitees) ? m.invitees.length : 0;
            const isUpcoming = m.status === 'scheduled' || m.status === 'live';
            const mid = (m.id || m._id || '').toString();
            return (
              <article key={m.id} className="ts-card">
                <h3 className="ts-card-title">{m.title || 'Meeting'}</h3>
                <div className="ts-card-badges">
                  {m.type === 'meeting' && m.allowAnyStudent && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">Open</span>
                  )}
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusBadge(m.status)}`}>{getStatusText(m.status)}</span>
                </div>
                <div className="ts-card-meta">
                  <span><FiCalendar size={12} /> {formatDate(m.scheduledAt || m.time)}</span>
                  <span><FiClock size={12} /> {m.durationMinutes || 60} min</span>
                  <span><FiUsers size={12} /> {inviteeCount} invitee{inviteeCount !== 1 ? 's' : ''}</span>
                </div>
                {m.description && <p style={{ fontSize: '0.8125rem', color: 'var(--ts-muted)', margin: '0 0 0.75rem 0', lineClamp: 2 }}>{m.description}</p>}
                <div className="ts-card-actions">
                  <Link to={`/meetings/${mid}`}>View details</Link>
                  {isUpcoming && (
                    <Link to={`/meetings/${mid}/join`} className="ts-primary">
                      {getActionLabel(m) === 'Start' ? <FiPlay size={14} /> : <FiVideo size={14} />}
                      {getActionLabel(m)}
                    </Link>
                  )}
                  {isUpcoming && canEndMeeting && m.type === 'meeting' && (
                    <button type="button" onClick={(e) => handleEndMeeting(e, m)} title="End meeting for all">
                      <FiSquare size={14} /> End
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {showCreateModal && (
        <div className="ts-modal-overlay" onClick={() => !createSaving && setShowCreateModal(false)}>
          <div className="ts-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ts-modal-title">{appName ? `${appName} – Create meeting` : 'Create meeting'}</div>
            <form onSubmit={handleCreateSubmit}>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                <select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value, invitees: [] }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400">
                  <option value="parent_teacher">Parent–Teacher</option>
                  {canCreateStaff && <option value="staff">Staff (internal)</option>}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
                <input type="text" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400" placeholder="e.g. Progress review" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Subject</label>
                <select value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400">
                  <option value="">— Select subject (optional) —</option>
                  {subjectOptions.map((name) => <option key={name} value={name}>{name}</option>)}
                </select>
                <p className="text-xs text-slate-500 mt-1">Subject and your name will be shown in the upper right of the meeting page.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Date & time *</label>
                <input type="datetime-local" value={form.scheduledAt} onChange={(e) => setForm((f) => ({ ...f, scheduledAt: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Duration (minutes)</label>
                <input type="number" min={5} max={480} value={form.durationMinutes} onChange={(e) => setForm((f) => ({ ...f, durationMinutes: parseInt(e.target.value, 10) || 60 }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400" placeholder="Optional" />
              </div>
              {form.type === 'parent_teacher' && (
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!form.allowAnyStudent} onChange={(e) => setForm((f) => ({ ...f, allowAnyStudent: e.target.checked }))} className="rounded border-slate-300 text-slate-900 focus:ring-slate-400" />
                    <span className="text-sm font-medium text-slate-700">Allow any student to join (share link with anyone)</span>
                  </label>
                  <p className="text-xs text-slate-500 mt-1">When checked, all students will see this meeting and can join via the link without being selected below.</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{form.type === 'parent_teacher' ? 'Invite specific people (optional)' : 'Invitees'}</label>
                <div className="border border-slate-200 rounded-lg px-3 py-2 max-h-40 overflow-y-auto space-y-1.5">
                  {inviteesLoading && <span className="text-slate-500 text-sm">Loading…</span>}
                  {!inviteesLoading && inviteesError && <span className="text-red-600 text-sm">{inviteesError}</span>}
                  {!inviteesLoading && !inviteesError && inviteesList.length === 0 && <span className="text-slate-500 text-sm">No invitees found for this type.</span>}
                  {!inviteesLoading && inviteesList.map((u) => (
                    <label key={u._id || u.id} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={form.invitees.includes(u._id || u.id)} onChange={() => toggleInvitee(u._id || u.id)} className="rounded border-slate-300 text-slate-900 focus:ring-slate-400" />
                      <span className="text-sm text-slate-700">{u.name || u.email} {u.role && <span className="text-slate-400">({u.role})</span>}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="ts-modal-actions">
                <button type="button" onClick={() => setShowCreateModal(false)} disabled={createSaving} className="ts-filter-btn">Cancel</button>
                <button type="submit" disabled={createSaving} className="ts-btn-primary">{createSaving ? 'Creating…' : 'Create meeting'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Meetings;
